import { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import Dropdown from '../components/Dropdown';
import Field from '../components/Field';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import TitleForm from '../components/TitleForm';
import VimeoMigrationModal from '../components/VimeoMigrationModal';
import ShelfFilters, { useShelfFilter } from '../components/ShelfFilters';
import { initialsOf } from '../components/BookShelf';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import useForm from '../hooks/useForm';
import * as contentRules from '../validation/contentRules';
import { api } from '../api/client';
import { formatBytes, uploadPercent } from '../bytes';
import { bySubject } from '../shelf';
import { canWriteContent } from '../roles';
import { useAuth } from '../context/AuthContext';

/**
 * Loading the repository, three screens deep: books, then that book's chapters, then
 * that chapter's files.
 *
 * <p>It used to be a book dropdown over two side-by-side panels. A dropdown names one
 * book at a time and says nothing about the rest, which is the wrong shape for the
 * question this screen is actually asked — *which books still have nothing in them* —
 * and it hid the covers that were loaded for exactly this purpose. So the library
 * opens on the shelf, grouped by subject the way a catalogue is, with each book saying
 * how far it has been loaded.
 *
 * <p>One level at a time rather than three panels: a chapter list and a file list
 * squeezed side by side gave each about a third of the width, and a chapter cover has
 * nowhere to go in a third of a column. Going in is a click and coming back is the
 * breadcrumb, which is also what makes a book's own header — its jacket, its code, its
 * counts — worth the room it takes.
 *
 * <p>The guideline defines no authoring screen for Phase 1, so this exists to make
 * Modules 8 and 9 usable rather than empty. The audience on each file is what decides
 * which of the two repositories shows it.
 *
 * <p>Support reads this screen and writes none of it: every control that changes
 * something is absent for them, and the API refuses the calls behind those controls
 * anyway. A file here reaches every teacher and student at once, so it is the one place
 * their access stops.
 */
export default function ContentManagerPage() {
  const { user } = useAuth();
  const canWrite = canWriteContent(user?.role);
  const [titles, setTitles] = useState([]);
  const [titleId, setTitleId] = useState('');
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState('');
  const [resources, setResources] = useState([]);
  // The title the modal is editing, 'new' while it is adding one, or null when shut.
  const [editingTitle, setEditingTitle] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [bookClasses, setBookClasses] = useState([]);
  // The chapter dialog: the chapter it is editing, or the flag that it is adding one.
  // Two pieces of state rather than a 'new' sentinel, because the add case carries no
  // row and the edit case must not be mistaken for one.
  const [editingChapter, setEditingChapter] = useState(null);
  const [addingChapter, setAddingChapter] = useState(false);
  // The same, for the file dialog.
  const [editingResource, setEditingResource] = useState(null);
  const [addingResource, setAddingResource] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  // The pending confirmation, or null. One slot rather than a flag per control: only
  // one of them can be open, and a flag each is how two dialogs end up on screen.
  const [confirming, setConfirming] = useState(null);
  // The one-time migration of a library that predates Vimeo. A modal rather than a
  // screen: it is a job you do once and then occasionally to retry a failure.
  const [showVimeo, setShowVimeo] = useState(false);

  const loadTitles = useCallback(async () => {
    // No auto-select of the first book any more: the shelf *is* the screen, and
    // landing inside whichever book sorts first hid it.
    setTitles(await api.contentTitles());
  }, []);

  const loadChapters = useCallback(async (id) => {
    setChapters(id ? await api.contentChapters(id) : []);
  }, []);

  const loadResources = useCallback(async (id) => {
    setResources(id ? await api.contentResources(id) : []);
  }, []);

  useEffect(() => {
    loadTitles().catch(setError);
    // For the title form. Failures are swallowed into an empty list rather than a
    // banner: a missing subject list leaves one dropdown empty, and the rest of the
    // library still works.
    api.lookupSubjects()
      .then((rows) => setSubjects(rows.map((row) => row.name)))
      .catch(() => setSubjects([]));
    api.lookupBookClasses().then(setBookClasses).catch(() => setBookClasses([]));
  }, [loadTitles]);

  // Stable, so the effects below can depend on them without re-running every render.
  const closeChapterForm = useCallback(() => {
    setEditingChapter(null);
    setAddingChapter(false);
  }, []);

  const closeResourceForm = useCallback(() => {
    setEditingResource(null);
    setAddingResource(false);
  }, []);

  useEffect(() => {
    setChapterId('');
    setResources([]);
    closeChapterForm();
    loadChapters(titleId).catch(setError);
  }, [titleId, loadChapters, closeChapterForm]);

  useEffect(() => {
    closeResourceForm();
    // Closed here as well as on the title: an Edit left open on the chapter grid would
    // otherwise follow you in and sit over the files of a chapter it is not editing.
    closeChapterForm();
    loadResources(chapterId).catch(setError);
  }, [chapterId, loadResources, closeChapterForm, closeResourceForm]);

  /**
   * Runs a change and reloads whatever it could have moved.
   *
   * <p>All three levels, because they are not independent: deleting a file changes its
   * chapter's count on the level above and its book's count on the level above that,
   * and a reload of only the level in front of you leaves the one you came from wrong
   * the moment you go back to it.
   */
  async function act(work, message) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await loadTitles();
      await loadChapters(titleId);
      await loadResources(chapterId);
      if (message) setToast(message);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Puts a confirmation in front of {@link act}.
   *
   * <p>In front of the three controls that reach past this screen while someone is
   * using what they change: retiring a chapter, retiring a file, and deleting one.
   * Retiring either used to be a ghost button that read like hiding a row, and since
   * the delivery gate started asking whether a chapter is still active it is not —
   * both kinds of retire revoke the links already in people's hands, so a video
   * playing this second stops.
   *
   * <p>Restoring is not confirmed, and neither is anything that only changes a label.
   * A dialog in front of every control would be a dialog nobody reads by the third
   * one, which would cost exactly the three it is here for.
   */
  function ask({ title, body, confirmLabel, danger, work, message }) {
    setConfirming({ title, body, confirmLabel, danger, work, message });
  }

  /**
   * Retiring or restoring a chapter, wherever the control is.
   *
   * <p>Two call sites — the chapter's own card and the header of the screen inside it
   * — and the sentence that warns about it is the whole value of the confirmation, so
   * it is written once. Two copies is how one of them stops mentioning the links.
   */
  function toggleChapter(chapter) {
    const work = () => api.updateChapter(chapter.chapterId, { active: !chapter.active });
    // Restoring puts a chapter back and needs no warning. Retiring is the one that
    // reaches people who are mid-lesson.
    if (!chapter.active) {
      act(work, 'Chapter restored');
      return;
    }
    ask({
      title: `Retire chapter ${chapter.sequence}?`,
      confirmLabel: 'Retire chapter',
      danger: true,
      work,
      message: 'Chapter retired',
      body: (
        <>
          <p>
            <b>{chapter.name}</b> disappears from every teacher&apos;s and
            student&apos;s repository, and so do the {chapter.resourceCount}{' '}
            {chapter.resourceCount === 1 ? 'file' : 'files'} in it — whatever each
            file&apos;s own status says.
          </p>
          <p className="hint">
            That includes anyone with the chapter already open: a link issued before now
            stops working, so a video playing right this moment stops. Nothing is
            deleted, and Restore puts it all back.
          </p>
        </>
      ),
    });
  }

  /**
   * The same for a book.
   *
   * <p>There is no delete and there will not be one: adoptions, activation codes,
   * chapters and every file are filed against a title, so the row cannot go without
   * taking all of that with it. Retiring is what taking a book out of circulation
   * means here — and it was reachable only from a modal on the Activation Codes
   * screen, which is nowhere anyone would look for it.
   */
  function toggleTitle(book) {
    const work = () => api.updateTitle(book.titleId, { active: !book.active });
    if (!book.active) {
      act(work, 'Title restored');
      return;
    }
    ask({
      title: `Retire ${book.name}?`,
      confirmLabel: 'Retire title',
      danger: true,
      work,
      message: 'Title retired',
      body: (
        <>
          <p>
            <b>{book.name}</b> disappears from every teacher&apos;s repository and from
            new adoptions, and no new activation codes can be minted for it.
          </p>
          <p className="hint">
            Nothing is deleted — its {book.chapterCount}{' '}
            {book.chapterCount === 1 ? 'chapter' : 'chapters'} and {book.resourceCount}{' '}
            {book.resourceCount === 1 ? 'file' : 'files'} stay where they are, schools
            that already adopted it keep their record, and Restore puts it all back. A
            student who has already activated it keeps it until their window closes.
          </p>
        </>
      ),
    });
  }

  // The shelf's two facets, shared with the student's own library.
  const { shelf, clearAll: clearFilters, filterProps } = useShelfFilter(titles);

  const selectedTitle = titles.find((title) => title.titleId === titleId);
  const selectedChapter = chapters.find((chapter) => chapter.chapterId === chapterId);
  // Which of the three screens we are on. Derived rather than stored: two ids and a
  // separate "level" are three things that can disagree, and the back-and-forth
  // between them is exactly where that would show.
  const level = !titleId ? 'books' : !chapterId ? 'chapters' : 'files';

  return (
    <>
      <div className="page-head row-between">
        <div>
          {/* The crumbs carry the going-back, so the heading names where you are
              rather than repeating "Content Library" three screens deep. */}
          <Crumbs
            trail={[
              { label: 'Content Library', onClick: titleId ? () => setTitleId('') : null },
              selectedTitle && {
                label: selectedTitle.name,
                onClick: chapterId ? () => setChapterId('') : null,
              },
              selectedChapter && { label: selectedChapter.name },
            ].filter(Boolean)}
          />
          <h1>
            {level === 'books'
              ? 'Content Library'
              : level === 'chapters'
                ? selectedTitle.name
                : selectedChapter.name}
          </h1>
          <p>
            {level === 'books'
              ? 'Every book in the catalogue, by subject. Open one to load its chapters.'
              : level === 'chapters'
                ? 'Chapters in this book. Open one to load its files.'
                : 'Files in this chapter. A file’s audience decides which repository shows it — teachers, students, or both.'}
          </p>
        </div>
        {level === 'books' && canWrite && (
          <span style={{ display: 'flex', gap: 'var(--s0)', flexWrap: 'wrap' }}>
            {/* Here rather than on a file row: moving a library that already exists
                is one job over the whole catalogue, and the per-row control is
                inside a single chapter. */}
            <button type="button" className="btn btn-secondary" onClick={() => setShowVimeo(true)}>
              Vimeo
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setEditingTitle('new')}>
              <Icon name="plus" size={15} />
              Add title
            </button>
          </span>
        )}
        {/* Only what belongs to the level you are on. Editing a book is done from the
            book's own card and editing a chapter from the chapter's, so a header
            duplicate of each was a second way to the same dialog — and on the files
            screen "Edit chapter" sat beside "Edit" controls on every row, meaning
            something different. */}
        {level === 'chapters' && canWrite && (
          <button type="button" className="btn btn-primary" onClick={() => setAddingChapter(true)}>
            <Icon name="plus" size={15} />
            Add a chapter
          </button>
        )}
        {level === 'files' && selectedChapter && canWrite && (
          <span style={{ display: 'flex', gap: 'var(--s0)', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => setAddingResource(true)}>
              <Icon name="upload" size={15} />
              Upload a file
            </button>
            <button
              type="button"
              className={`btn ${selectedChapter.active ? 'btn-danger' : 'btn-secondary'}`}
              disabled={busy}
              onClick={() => {
                const work = () =>
                  api.updateChapter(chapterId, { active: !selectedChapter.active });
                // Restoring puts a chapter back and needs no warning. Retiring is the
                // one that reaches people who are mid-lesson.
                if (!selectedChapter.active) {
                  act(work, 'Chapter restored');
                  return;
                }
                ask({
                  title: `Retire chapter ${selectedChapter.sequence}?`,
                  confirmLabel: 'Retire chapter',
                  danger: true,
                  work,
                  message: 'Chapter retired',
                  body: (
                    <>
                      <p>
                        <b>{selectedChapter.name}</b> disappears from every teacher&apos;s
                        and student&apos;s repository, and so do the{' '}
                        {selectedChapter.resourceCount}{' '}
                        {selectedChapter.resourceCount === 1 ? 'file' : 'files'} in it —
                        whatever each file&apos;s own status says.
                      </p>
                      <p className="hint">
                        That includes anyone with the chapter already open: a link issued
                        before now stops working, so a video playing right this moment
                        stops. Nothing is deleted, and Restore puts it all back.
                      </p>
                    </>
                  ),
                });
              }}
            >
              {selectedChapter.active ? 'Retire chapter' : 'Restore chapter'}
            </button>
          </span>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {!canWrite && level === 'books' && (
        <InfoBanner>
          You can read the library and not change it. Adding or updating a title, chapter
          or file is the Super Admin&apos;s — one change here reaches every teacher and
          student at once.
        </InfoBanner>
      )}

      {/* ── the shelf ─────────────────────────────────────────────────── */}
      {level === 'books' && (
        <>
          {titles.length === 0 && (
            <InfoBanner>
              There are no titles yet, and content is always filed under one.
              {canWrite ? ' Add the first one above.' : ' A Super Admin adds the first one.'}
            </InfoBanner>
          )}
          <ShelfFilters {...filterProps} />

          {shelf.filtered && shelf.matches.length === 0 && (
            <div className="card empty">
              No book matches this filter.{' '}
              <button type="button" className="link-button" onClick={clearFilters}>
                Clear it
              </button>
              .
            </div>
          )}

          {bySubject(shelf.matches).map(([subject, books]) => (
            <section key={subject} style={{ marginBottom: 'var(--s2)' }}>
              <h2 className="shelf-group">
                {subject}
                <span className="count">
                  {books.length} {books.length === 1 ? 'book' : 'books'}
                </span>
              </h2>
              <div className="shelf shelf-sm">
                {books.map((book) => (
                  <article className={`book${book.active ? '' : ' book-retired'}`} key={book.titleId}>
                    <button
                      type="button"
                      className="book-cover jacket"
                      aria-label={`Open ${book.name}`}
                      onClick={() => setTitleId(book.titleId)}
                    >
                      {book.coverUrl ? (
                        <img src={book.coverUrl} alt="" loading="lazy" />
                      ) : (
                        <span className="book-initials">
                          {initialsOf(book.shortName || book.name)}
                        </span>
                      )}
                    </button>
                    <div className="book-body">
                      <b>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setTitleId(book.titleId)}
                        >
                          {book.name}
                        </button>
                      </b>
                      <span className="muted small">
                        {[book.classLevel, book.code].filter(Boolean).join(' · ') || '—'}
                      </span>
                      <div className="book-foot">
                        {/* The two counts are the whole reason this shelf exists: a
                            catalogue of forty books is loaded over months, and "which
                            ones are still empty" was unanswerable from a dropdown. */}
                        <span className="tiny muted">
                          {book.chapterCount === 0
                            ? 'No chapters yet'
                            : `${book.chapterCount} ${book.chapterCount === 1 ? 'chapter' : 'chapters'} · ${book.resourceCount} ${book.resourceCount === 1 ? 'file' : 'files'}`}
                        </span>
                        <span style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          {!book.active && <span className="badge badge-danger">Retired</span>}
                          {/* Correcting a title no longer means opening the book
                              first: a typo in a name or a book filed under the wrong
                              subject is spotted here, on the screen that lists it. */}
                          {canWrite && (
                            <button
                              type="button"
                              className="icon-btn"
                              aria-label={`Edit ${book.name}`}
                              title="Edit title"
                              onClick={() => setEditingTitle(book)}
                            >
                              <Icon name="pencil" size={15} />
                            </button>
                          )}
                          {/* An eye, because retiring is what taking a book out of
                              sight means here — there is no delete, and there cannot
                              be one while adoptions and codes are filed against it. */}
                          {canWrite && (
                            <button
                              type="button"
                              className="icon-btn"
                              disabled={busy}
                              aria-label={`${book.active ? 'Retire' : 'Restore'} ${book.name}`}
                              title={book.active ? 'Retire title' : 'Restore title'}
                              onClick={() => toggleTitle(book)}
                            >
                              <Icon name={book.active ? 'eye-off' : 'eye'} size={15} />
                            </button>
                          )}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* ── one book's chapters ───────────────────────────────────────── */}
      {level === 'chapters' && selectedTitle && (
        <>
          <BookHeader book={selectedTitle} />

          {chapters.length === 0 && (
            <InfoBanner>
              No chapters in this book yet.
              {canWrite ? ' Add the first one above.' : ''}
            </InfoBanner>
          )}

          <div className="shelf shelf-sm" style={{ marginBottom: 'var(--s2)' }}>
            {chapters.map((chapter) => (
              <article
                className={`book${chapter.active ? '' : ' book-retired'}`}
                key={chapter.chapterId}
              >
                <button
                  type="button"
                  className="book-cover"
                  aria-label={`Open chapter ${chapter.sequence}`}
                  onClick={() => setChapterId(chapter.chapterId)}
                >
                  {chapter.coverUrl ? (
                    <img src={chapter.coverUrl} alt="" loading="lazy" />
                  ) : (
                    // What it is, not just where it sits: a grid of tiles reading
                    // 1, 2, 3 names nothing, and initials would be worse — a
                    // chapter's identity is its position in the book.
                    <span className="tile-label">Chapter {chapter.sequence}</span>
                  )}
                </button>
                <div className="book-body">
                  <b>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setChapterId(chapter.chapterId)}
                    >
                      {chapter.name}
                    </button>
                  </b>
                  <div className="book-foot">
                    {/* The number moved off the front of the name and down here, with
                        the count. "4. Fractions and Decimals" read as part of the
                        title; this is the same fact filed as what it is. */}
                    <span className="tiny muted">
                      Chapter {chapter.sequence} · {chapter.resourceCount}{' '}
                      {chapter.resourceCount === 1 ? 'file' : 'files'}
                    </span>
                    <span style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {!chapter.active && <span className="badge badge-danger">Retired</span>}
                      {canWrite && (
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Edit chapter ${chapter.sequence}`}
                          title="Edit chapter"
                          onClick={() => setEditingChapter(chapter)}
                        >
                          <Icon name="pencil" size={15} />
                        </button>
                      )}
                      {canWrite && (
                        <button
                          type="button"
                          className="icon-btn"
                          disabled={busy}
                          aria-label={`${chapter.active ? 'Retire' : 'Restore'} chapter ${chapter.sequence}`}
                          title={chapter.active ? 'Retire chapter' : 'Restore chapter'}
                          onClick={() => toggleChapter(chapter)}
                        >
                          <Icon name={chapter.active ? 'eye-off' : 'eye'} size={15} />
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

        </>
      )}

      {/* ── one chapter's files ───────────────────────────────────────── */}
      {level === 'files' && selectedChapter && (
        <>
          <ChapterHeader chapter={selectedChapter} book={selectedTitle} />

          <section className="card" style={{ marginBottom: 'var(--s2)' }}>
            {resources.length === 0 && (
              <div className="empty">
                No files in this chapter yet.
                {canWrite ? ' Upload the first one above.' : ''}
              </div>
            )}

            {resources.map((resource) => (
              <div className="res-row" key={resource.resourceId}>
                <span className="res-icon" aria-hidden="true">
                  <Icon name="doc" size={17} />
                </span>
                <span className="res-main">
                  <b>{resource.name}</b>
                  <span className="muted small">
                    {resource.typeLabel} · {resource.audienceLabel}
                    {/* The size belongs on the row rather than only inside the edit
                        form: "is the whole video up?" is the question this screen gets
                        asked, and it was two clicks away. */}
                    {formatBytes(resource.sizeBytes) ? ` · ${formatBytes(resource.sizeBytes)}` : ''}
                    {resource.downloadable ? ' · downloadable' : ''}
                    {resource.active ? '' : ' · retired'}
                  </span>
                  {/* Only the admin screen shows this. A video that is not ready is
                      not listed to a teacher or a student at all, so without it here
                      an upload would simply vanish for twenty minutes. */}
                  {resource.vimeoStatus && resource.vimeoStatus !== 'READY' && (
                    <span
                      className={`badge ${resource.vimeoStatus === 'FAILED' ? 'badge-danger' : 'badge-warn'}`}
                      style={{ marginTop: 4 }}
                    >
                      {resource.vimeoStatusLabel}
                    </span>
                  )}
                </span>
                {canWrite && (
                  <span style={{ display: 'flex', gap: 2, whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => setEditingResource(resource)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => {
                        const work = () =>
                          api.updateResource(resource.resourceId, { active: !resource.active });
                        // Restoring puts a file back and warns about nothing.
                        if (!resource.active) {
                          act(work, 'File restored');
                          return;
                        }
                        ask({
                          title: 'Retire this file?',
                          confirmLabel: 'Retire file',
                          danger: true,
                          work,
                          message: 'File retired',
                          body: (
                            <>
                              <p>
                                <b>{resource.name}</b> disappears from{' '}
                                {resource.audience === 'BOTH'
                                  ? 'both repositories'
                                  : resource.audience === 'TEACHER'
                                    ? 'the teacher repository'
                                    : 'the student repository'}
                                , and a link issued for it before now stops working — so
                                anyone with it open right this moment loses it.
                              </p>
                              <p className="hint">
                                Nothing is deleted. Restore, in this same spot, puts it
                                back.
                              </p>
                            </>
                          ),
                        });
                      }}
                    >
                      {resource.active ? 'Retire' : 'Restore'}
                    </button>
                    {/* How content uploaded before Vimeo was configured gets there,
                        and how a failed push is retried. The server decides whether
                        it is possible; this only decides whether to offer it. */}
                    {resource.canSendToVimeo && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () => api.sendResourceToVimeo(resource.resourceId),
                            `“${resource.name}” is on its way to Vimeo`,
                          )
                        }
                      >
                        {resource.vimeoStatus === 'FAILED' ? 'Retry Vimeo' : 'Send to Vimeo'}
                      </button>
                    )}
                    {/* Delete has been in the API since the start and was reachable from
                        nowhere, so a file uploaded under the wrong book could only ever
                        be hidden — it stayed in the list, in the count, and on disk,
                        forever. */}
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={busy}
                      onClick={() =>
                        ask({
                          title: 'Delete this file?',
                          confirmLabel: 'Delete file',
                          danger: true,
                          work: () => api.deleteResource(resource.resourceId),
                          message: `“${resource.name}” deleted`,
                          body: (
                            <>
                              <p>
                                <b>{resource.name}</b> and the uploaded file itself are
                                removed. There is no undo and no copy kept — it would have
                                to be uploaded again.
                              </p>
                              <p className="hint">
                                To take it out of circulation but keep it, use Retire
                                instead.
                              </p>
                            </>
                          ),
                        })
                      }
                    >
                      Delete
                    </button>
                  </span>
                )}
              </div>
            ))}
          </section>

        </>
      )}

      {/* Every form on this screen is a dialog. They were sections stacked under the
          list they belonged to, which put the one control people came to use below a
          grid of thirty tiles — and on the files screen it meant a 512 MB upload ran
          in a panel that scrolled out of sight. A dialog is where the work is. */}
      {canWrite && (addingChapter || editingChapter) && (
        <ChapterForm
          key={editingChapter?.chapterId ?? 'new'}
          titleId={titleId}
          chapter={editingChapter}
          nextSequence={(chapters.at(-1)?.sequence ?? 0) + 1}
          onCancel={closeChapterForm}
          onSaved={async (name, added) => {
            closeChapterForm();
            await loadChapters(titleId);
            await loadTitles();
            setToast(added ? `Chapter “${name}” added` : `Chapter “${name}” saved`);
          }}
          onError={setError}
        />
      )}

      {canWrite && (addingResource || editingResource) && (
        <ResourceForm
          key={editingResource?.resourceId ?? 'new'}
          chapterId={chapterId}
          resource={editingResource}
          onCancel={closeResourceForm}
          onSaved={async (name, outcome) => {
            closeResourceForm();
            await loadResources(chapterId);
            await loadChapters(titleId);
            await loadTitles();
            setToast(
              outcome === 'added'
                ? `“${name}” uploaded`
                : outcome === 'replaced'
                  ? `“${name}” now holds the new file`
                  : `“${name}” saved`,
            );
          }}
          onError={setError}
        />
      )}

      {editingTitle && (
        <Modal
          title={editingTitle === 'new' ? 'Add a title' : `Edit ${editingTitle.name}`}
          subtitle="Adoptions, activation codes and resources are all filed against these."
          width={620}
          onClose={() => setEditingTitle(null)}
        >
          <TitleForm
            // The form answers with TitleDtos' shape, so what it is handed has to be
            // that shape too — the shelf's rows name the id `titleId`.
            title={
              editingTitle === 'new' ? null : { ...editingTitle, id: editingTitle.titleId }
            }
            subjects={subjects}
            bookClasses={bookClasses}
            onCancel={() => setEditingTitle(null)}
            onSaved={async () => {
              setEditingTitle(null);
              await loadTitles();
              setToast('Title saved');
            }}
            onError={setError}
          />
        </Modal>
      )}

      {showVimeo && (
        <VimeoMigrationModal
          onClose={() => setShowVimeo(false)}
          onChanged={() => {
            // A file that starts moving stops being listed to teachers and students,
            // so the counts behind this modal change as it works.
            loadTitles().catch(setError);
            loadResources(chapterId).catch(setError);
          }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={confirming.title}
          confirmLabel={confirming.confirmLabel}
          danger={confirming.danger}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            // Stays up while the call is in flight — that is what `busy` disables the
            // buttons for, and a second click on Delete would otherwise 404. Closed
            // afterwards either way: act swallows the failure into the banner, and the
            // banner is behind this.
            await act(confirming.work, confirming.message);
            setConfirming(null);
          }}
        >
          {confirming.body}
        </ConfirmDialog>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2400)} />
    </>
  );
}

/**
 * Where you are, and the way back out.
 *
 * <p>A back button would do one of these and not the other. Three levels deep, "which
 * book am I in" is a question the heading alone cannot answer once you are looking at
 * a chapter called "Fractions and Decimals" that four books could have.
 */
function Crumbs({ trail }) {
  if (trail.length < 2) return null;
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {trail.map((crumb, index) => (
        <span key={crumb.label}>
          {index > 0 && <span aria-hidden="true"> / </span>}
          {crumb.onClick ? (
            <button type="button" className="link-button" onClick={crumb.onClick}>
              {crumb.label}
            </button>
          ) : (
            <span aria-current="page">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** The book you are inside, as its jacket and the facts that identify it. */
function BookHeader({ book }) {
  return (
    <section className="card card-pad lib-head" style={{ marginBottom: 'var(--s2)' }}>
      {/* Portrait, because a book jacket is: the landscape frame contained it to half
          its own width and left two bands of empty lilac either side. */}
      <span className="lib-cover jacket">
        {book.coverUrl ? (
          <img src={book.coverUrl} alt="" />
        ) : (
          <span className="book-initials">{initialsOf(book.shortName || book.name)}</span>
        )}
      </span>
      <div>
        <b>{book.name}</b>
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          {[book.subject, book.classLevel, book.code].filter(Boolean).join(' · ') || '—'}
        </p>
        <p className="tiny muted" style={{ margin: '4px 0 0' }}>
          {book.chapterCount} {book.chapterCount === 1 ? 'chapter' : 'chapters'} ·{' '}
          {book.resourceCount} {book.resourceCount === 1 ? 'file' : 'files'}
          {book.active ? '' : ' · retired'}
        </p>
      </div>
    </section>
  );
}

/** The chapter you are inside, with the book above it named rather than assumed. */
function ChapterHeader({ chapter, book }) {
  return (
    <section className="card card-pad lib-head" style={{ marginBottom: 'var(--s2)' }}>
      <span className="lib-cover">
        {chapter.coverUrl ? (
          <img src={chapter.coverUrl} alt="" />
        ) : (
          <span className="tile-label">Chapter {chapter.sequence}</span>
        )}
      </span>
      <div>
        <b>{chapter.name}</b>
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          Chapter {chapter.sequence}
          {book?.name ? ` · ${book.name}` : ''}
        </p>
        <p className="tiny muted" style={{ margin: '4px 0 0' }}>
          {chapter.resourceCount} {chapter.resourceCount === 1 ? 'file' : 'files'}
          {chapter.active ? '' : ' · retired'}
        </p>
      </div>
    </section>
  );
}

/**
 * Adding a chapter and editing one, in the same form.
 *
 * <p>Renaming and reordering were already in the API and reachable from nowhere; the
 * cover needed somewhere to live, and a second form beside the first would have been
 * two places to forget a field. Retiring stays on the file panel, where the chapter
 * being retired is the one on screen.
 *
 * <p>The cover follows the same rule as a book's: on an edit it goes up as soon as it
 * is chosen, and on a create it waits, because there is no chapter to attach it to
 * until the chapter exists.
 */
function ChapterForm({ titleId, chapter, nextSequence, onCancel, onSaved, onError }) {
  const editingId = chapter?.chapterId ?? null;
  const [coverUrl, setCoverUrl] = useState(chapter?.coverUrl ?? null);
  const [pendingCover, setPendingCover] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const inputId = `chapter-cover-${editingId ?? 'new'}`;
  const formId = `chapter-form-${editingId ?? 'new'}`;

  const form = useForm({
    initialValues: chapter
      ? { name: chapter.name ?? '', sequence: String(chapter.sequence ?? '') }
      : { name: '', sequence: String(nextSequence) },
    validators: { name: contentRules.chapterName, sequence: contentRules.sequence },
  });

  const { reset } = form;
  useEffect(() => {
    // Only while adding: the next free number moves as chapters are added, and
    // re-seeding an edit from it would renumber the chapter being edited.
    if (!editingId) reset({ name: '', sequence: String(nextSequence) });
  }, [editingId, nextSequence, reset]);

  async function chooseCover(file) {
    if (!file) return;
    if (!editingId) {
      setPendingCover(file);
      setCoverUrl(URL.createObjectURL(file));
      return;
    }
    setCoverBusy(true);
    try {
      const updated = await api.uploadChapterCover(editingId, file);
      setCoverUrl(updated.coverUrl);
    } catch (err) {
      onError(err);
    } finally {
      setCoverBusy(false);
    }
  }

  async function dropCover() {
    if (!editingId) {
      setPendingCover(null);
      setCoverUrl(null);
      return;
    }
    setCoverBusy(true);
    try {
      const updated = await api.removeChapterCover(editingId);
      setCoverUrl(updated.coverUrl ?? null);
    } catch (err) {
      onError(err);
    } finally {
      setCoverBusy(false);
    }
  }

  const submit = form.handleSubmit(async (values) => {
    const name = values.name.trim();
    const body = { name, sequence: Number(values.sequence) };
    try {
      if (editingId) {
        await api.updateChapter(editingId, body);
        await onSaved(name, false);
        return;
      }
      const created = await api.createChapter(titleId, body);
      if (pendingCover) {
        try {
          await api.uploadChapterCover(created.chapterId, pendingCover);
        } catch (err) {
          // The chapter exists; say which half failed rather than implying neither did.
          onError(err);
        }
      }
      setPendingCover(null);
      setCoverUrl(null);
      form.reset({ name: '', sequence: String(nextSequence + 1) });
      await onSaved(name, true);
    } catch (err) {
      if (!form.applyServerErrors(err, contentRules.SERVER_CODES)) onError(err);
    }
  });

  return (
    <Modal
      title={editingId ? `Edit chapter ${chapter.sequence}` : 'Add a chapter'}
      width={560}
      // Not while a cover is going up: the upload is against a chapter that exists,
      // and dismissing mid-flight leaves the form's idea of the cover behind.
      onClose={coverBusy || form.submitting ? () => {} : onCancel}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={form.submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          {/* The footer sits outside the form element, so the button is tied back to
              it by id — the same way the password modals do it. */}
          <button type="submit" form={formId} className="btn btn-primary" disabled={form.submitting}>
            <Icon name={editingId ? 'check' : 'plus'} size={15} />
            {form.submitting ? 'Saving…' : editingId ? 'Save chapter' : 'Add chapter'}
          </button>
        </>
      }
    >
      <form id={formId} {...form.formProps} onSubmit={submit} className="stack">
        <div className="grid-2">
          <Field label="Chapter name" id="name" required error={form.errorFor('name')}>
            <input className="input" maxLength={200} {...form.field('name')} />
          </Field>
          <Field label="Number" id="sequence" required error={form.errorFor('sequence')}>
            <input className="input" inputMode="numeric" maxLength={3} {...form.field('sequence')} />
          </Field>
        </div>

        <Field label="Cover image" id={inputId} hint="PNG, JPEG or WebP, up to 2 MB. Optional.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s1)' }}>
            <span className="chapter-thumb">
              {coverUrl ? <img src={coverUrl} alt="" /> : <span className="tiny muted">None</span>}
            </span>
            <span style={{ display: 'flex', gap: 'var(--s0)' }}>
              <label htmlFor={inputId} className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                {coverBusy ? 'Uploading…' : coverUrl ? 'Replace' : 'Choose image'}
              </label>
              <input
                id={inputId}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                disabled={coverBusy}
                onChange={(event) => chooseCover(event.target.files?.[0])}
              />
              {coverUrl && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={coverBusy} onClick={dropCover}>
                  Remove
                </button>
              )}
            </span>
          </div>
        </Field>

      </form>
    </Modal>
  );
}

/**
 * How far an upload has got.
 *
 * <p>A {@code <progress>} element would be fewer lines and is styled by four different
 * rulesets across browsers, none of which is this one. A div with a width is.
 *
 * <p>No aria-live. The percentage changes several times a second and a polite live
 * region would read every one of them out; {@code role="progressbar"} is the semantic
 * that lets a screen reader report the value when it is asked for it instead.
 */
function UploadBar({ progress }) {
  if (!progress) return null;
  const percent = uploadPercent(progress);
  // Either the browser told us the body is away, or the arithmetic got there first.
  const finishing = Boolean(progress.sent) || percent === 100;
  const sizes = progress.total ? formatBytes(progress.total) : null;

  return (
    <div className="upload-progress">
      <div
        className="upload-track"
        role="progressbar"
        aria-label="Upload progress"
        aria-valuemin={0}
        aria-valuemax={100}
        // Omitted rather than guessed when the browser could not say how big the body
        // is: an indeterminate progressbar has no value, and 0 would be a lie.
        aria-valuenow={finishing ? 100 : percent ?? undefined}
      >
        <div
          className={`upload-fill${finishing ? ' finishing' : ''}`}
          style={finishing ? undefined : { width: `${percent ?? 0}%` }}
        />
      </div>
      <span className="tiny muted">
        {finishing
          ? 'Uploaded — the server is still saving it. Don’t close this tab.'
          : percent === null
            ? `${formatBytes(progress.loaded) ?? '0 bytes'} sent`
            : `${percent}% · ${formatBytes(progress.loaded)} of ${sizes}`}
      </span>
    </div>
  );
}

/**
 * Uploading a file into a chapter, and correcting one already there.
 *
 * <p>Correcting was reachable from nowhere. PATCH has always accepted a rename, a
 * retype, a change of audience and the downloadable flag; the screen offered Retire
 * and Restore, so a file published to the wrong audience — a teacher manual filed as
 * student reading — could be hidden and re-uploaded but not fixed. The same form does
 * both, because a field added to one of two forms is a field the other cannot correct.
 *
 * <p>Replacing the bytes is now possible too, and it is deliberately not part of Save.
 * It goes out on its own call the moment it is confirmed, because it is a different act
 * from fixing a label and has a consequence no other control here has: the resource id
 * does not change, a signed link names a resource id, so a link handed out in the last
 * few minutes starts serving the new file. That is what makes it worth having — a wrong
 * chapter 4 video is corrected for everyone at once — and what makes it worth asking
 * about first.
 */
function ResourceForm({ chapterId, resource, onCancel, onSaved, onError }) {
  const editingId = resource?.resourceId ?? null;
  const [file, setFile] = useState(null);
  // Partial updates from the client merged over what we already know: the last one says
  // only `sent`, and the total it was counting towards is still worth showing.
  const [progress, setProgress] = useState(null);
  const advance = (update) => setProgress((current) => ({ ...current, ...update }));
  // The replacement waiting on its confirmation, and whether one is in flight.
  const [replacement, setReplacement] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const fileInputId = `resource-file-${editingId ?? 'new'}`;
  const formId = `resource-form-${editingId ?? 'new'}`;

  const form = useForm({
    initialValues: resource
      ? {
          name: resource.name ?? '',
          type: resource.type ?? 'LESSON_PLAN',
          audience: resource.audience ?? 'TEACHER',
          downloadable: Boolean(resource.downloadable),
        }
      : { name: '', type: 'LESSON_PLAN', audience: 'TEACHER', downloadable: false },
    validators: { name: contentRules.resourceName },
    crossField: (values) => ({
      type: contentRules.typeSuitsAudience(values.type, values.audience),
    }),
  });

  const submit = form.handleSubmit(async (values) => {
    const body = {
      name: values.name.trim(),
      type: values.type,
      audience: values.audience,
      downloadable: Boolean(values.downloadable),
    };
    try {
      if (editingId) {
        await api.updateResource(editingId, body);
        await onSaved(body.name, 'saved');
        return;
      }
      if (!file) {
        onError({ message: 'Choose a file to upload', fieldErrors: [] });
        return;
      }
      // Seeded from the file rather than waiting for the first progress event, so the
      // bar appears with the size in it on the click instead of a beat later.
      setProgress({ loaded: 0, total: file.size, sent: false });
      await api.uploadResource(chapterId, body, file, advance);
      form.reset();
      setFile(null);
      setProgress(null);
      await onSaved(body.name, 'added');
    } catch (err) {
      // Cleared on the way out: a bar left at 38% beside an error banner reads as
      // "still going", and the next attempt starts from zero.
      setProgress(null);
      if (!form.applyServerErrors(err, contentRules.SERVER_CODES)) onError(err);
    }
  });

  /** Sends the confirmed replacement. The labels on the form are untouched by it. */
  async function replaceFile() {
    const chosen = replacement;
    setReplacement(null);
    setReplacing(true);
    setProgress({ loaded: 0, total: chosen.size, sent: false });
    try {
      await api.replaceResourceFile(editingId, chosen, advance);
      setProgress(null);
      await onSaved(resource.name, 'replaced');
    } catch (err) {
      setProgress(null);
      onError(err);
    } finally {
      setReplacing(false);
    }
  }

  // Only teacher-only content can be downloadable, and never video, audio or an
  // activity. Video and audio live on Vimeo, where there is no file of ours to hand
  // over; an activity's file is the whole activity, which runs anywhere once saved.
  // The server clears the flag in every case, so the control disappears rather than
  // lying about what will happen.
  const canBeDownloadable =
    form.values.audience === 'TEACHER'
    && !contentRules.isVideoType(form.values.type)
    && !contentRules.isActivityType(form.values.type);

  // The dialog must not be dismissable mid-transfer. Escape and a click outside are
  // both "cancel", and cancelling a 512 MB upload by brushing the backdrop at 90% is
  // the one mistake this screen can make that costs four minutes.
  const inFlight = form.submitting || replacing;

  return (
    <>
      <Modal
        title={editingId ? `Edit ${resource.name}` : 'Upload a file'}
        width={620}
        onClose={inFlight ? () => {} : onCancel}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={inFlight}
              onClick={onCancel}
            >
              Cancel
            </button>
            {/* Outside the form element, so tied back to it by id. */}
            <button type="submit" form={formId} className="btn btn-primary" disabled={inFlight}>
              <Icon name={editingId ? 'check' : 'upload'} size={15} />
              {form.submitting ? 'Saving…' : editingId ? 'Save file' : 'Upload'}
            </button>
          </>
        }
      >
        <form id={formId} {...form.formProps} onSubmit={submit} className="stack">
          <Field label="File name" id="name" required error={form.errorFor('name')}>
            <input className="input" maxLength={200} {...form.field('name')} />
          </Field>

          <div className="grid-2">
            <Field label="Audience" id="audience">
              <Dropdown
                id="audience"
                required
                options={contentRules.AUDIENCES}
                value={form.values.audience}
                onChange={(audience) => {
                  form.setValue('audience', audience);
                  // The type list changes with the audience; leaving an incompatible
                  // type selected would show a type this audience cannot have.
                  if (!contentRules.allows(form.values.type, audience)) {
                    form.setValue('type', contentRules.typesFor(audience)[0].value);
                  }
                  if (audience !== 'TEACHER') form.setValue('downloadable', false);
                }}
              />
            </Field>
            <Field label="Type" id="type" error={form.errorFor('type')}>
              <Dropdown
                id="type"
                required
                options={contentRules.typesFor(form.values.audience)}
                value={form.values.type}
                onChange={(type) => form.setValue('type', type)}
                error={form.errorFor('type')}
                describedBy={form.errorFor('type') ? 'type-error' : undefined}
              />
            </Field>
          </div>

          {editingId ? (
            <Field label="File" id={fileInputId}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s1)', flexWrap: 'wrap' }}>
                <span className="small">
                  {resource.contentType}
                  {formatBytes(resource.sizeBytes) ? ` · ${formatBytes(resource.sizeBytes)}` : ''}
                </span>
                {/* A label over a hidden input, not a bare file input: choosing a file here
                    does not stage a change the way the create form does — it goes out as
                    soon as it is confirmed, and a button is what that behaves like. */}
                <label
                  htmlFor={fileInputId}
                  className="btn btn-secondary btn-sm"
                  style={{ cursor: replacing ? 'default' : 'pointer' }}
                >
                  {replacing ? 'Replacing…' : 'Replace file'}
                </label>
                <input
                  id={fileInputId}
                  type="file"
                  style={{ display: 'none' }}
                  disabled={replacing}
                  // Narrowed to the kind this resource can hold, so the rule the server
                  // enforces is one the picker never lets you break.
                  accept={contentRules.acceptFor(resource.type)}
                  onChange={(event) => {
                    setReplacement(event.target.files?.[0] ?? null);
                    // Cleared so picking the same file twice fires change again — after a
                    // cancelled confirmation the input still holds it, and without this the
                    // second attempt does nothing at all.
                    event.target.value = '';
                  }}
                />
              </div>
              <p className="hint" style={{ margin: '6px 0 0' }}>
                A replacement goes out on its own, as soon as you confirm it — it is not
                held until you press Save file. The fields above are, and a type changed
                up there has to be saved before it decides what this will accept.
              </p>
            </Field>
          ) : (
            <Field
            label="File"
            id="file"
            hint={
              contentRules.isVideoType(form.values.type)
                ? 'MP4 or MOV. This goes to Vimeo — an audio lesson has to be a video file too (the recording over a still image), because Vimeo will not accept audio on its own.'
                : contentRules.isActivityType(form.values.type)
                  ? 'A ZIP of the activity folder: index.html at the top of it, with the img and audio folders beside it. One activity per zip — if the zip holds two, the upload is refused and names them.'
                  : 'PDF, PPTX, DOCX, PNG or JPEG'
            }
          >
            {/* Narrowed to the kind this type takes, the same way the replace picker
                is — both from contentRules, so a new kind cannot be added to one and
                forgotten in the other. */}
            <input
              id="file"
              className="input"
              type="file"
              accept={contentRules.acceptFor(form.values.type)}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>
          )}

          <UploadBar progress={progress} />

          {canBeDownloadable && (
            <label className="radio" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(form.values.downloadable)}
                onChange={(event) => form.setValue('downloadable', event.target.checked)}
              />
              <span className="dot" />
              <span>Teachers may download this file</span>
            </label>
          )}

        </form>
      </Modal>

      {/* Outside the Modal, not inside it: both are fixed overlays at the same
          z-index, so the one later in the DOM is the one on top. */}
      {replacement && (
        <ConfirmDialog
          title="Replace this file?"
          confirmLabel="Replace file"
          danger
          onCancel={() => setReplacement(null)}
          onConfirm={replaceFile}
        >
          <p>
            <b>{resource.name}</b> will serve <b>{replacement.name}</b>
            {formatBytes(replacement.size) ? ` (${formatBytes(replacement.size)})` : ''} instead.
            The file it holds now is deleted.
          </p>
          <p className="hint">
            Everyone sees the new file the next time they open it — including anyone who
            opened the old one in the last few minutes, because a link is issued for the
            resource rather than for the file behind it. A video playing right now may
            jump to the new one.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}
